import {ref} from "vue";
import {AxiosError} from "axios";
import {aiAssistantApi} from "@/container";
import {useAiAssistantStore} from "@/stores/aiAssistant.store";
import {Proposition} from "@/ai-assistant/interfaces";
import {DECISION_ERRORS, DECISION_GENERIC_ERROR} from "@/ai-assistant/constants";
import {invalidatePlanningRange} from "@/planning/composables/usePlanningRange";
import {invalidateAssistantHistory} from "@/ai-assistant/composables/useAssistantHistory";

/**
 * Apply or reject one scenario.
 *
 * Nothing here decides anything: both calls are made from a click, and there
 * is no path that writes without one. That is what the module's framing rests
 * on, and what keeps a pharmacy's planning out of reach of an automated
 * individual decision.
 */
const useAssistantDecision = () => {
    const store = useAiAssistantStore();

    const pending = ref<boolean>(false);
    const failure = ref<string | null>(null);

    /**
     * Whether the refusal was the planning having moved.
     *
     * Surfaced apart because the answer differs: a scenario that is merely out
     * of date is regenerated by asking again, where another failure is retried.
     */
    const expired = ref<boolean>(false);

    const decide = async (decision: () => Promise<Proposition>): Promise<boolean> => {
        pending.value = true;
        failure.value = null;
        expired.value = false;

        try {
            store.replaceProposition(await decision());

            // The history panel may be open above the conversation: a decision
            // taken here has to reach it.
            invalidateAssistantHistory();

            return true;
        } catch (error) {
            const status = (error as AxiosError)?.response?.status ?? 0;

            expired.value = status === 409;
            failure.value = DECISION_ERRORS[status] ?? DECISION_GENERIC_ERROR;

            return false;
        } finally {
            pending.value = false;
        }
    };

    /**
     * Applying writes dated overrides, so the screens showing them have to
     * read their period again — the agenda's presence rail above all, which
     * sits beside this panel and would otherwise keep the previous hours.
     */
    const apply = async (propositionId: number) => {
        const applied = await decide(() => aiAssistantApi().applyProposition(propositionId));

        if (applied) {
            invalidatePlanningRange();
        }

        return applied;
    };

    const reject = (propositionId: number) =>
        decide(() => aiAssistantApi().rejectProposition(propositionId));

    return {pending, failure, expired, apply, reject};
};

export default useAssistantDecision;
